import { useEffect } from "react";
import { Button, LinkButton } from "../common/Button";
import { Icon } from "../common/Icon";
import { useSettings } from "./SettingsProvider";
import { t } from "../../lib/i18n";
import { CALC_METHODS } from "../../lib/api/adhanCalc";
import type { AccentTheme, CalcMethodKey, Locale, MotionPreference, TextSize, ThemeMode, UserSettings, VisualStyle } from "../../types/app";

interface SettingsPanelProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

const themeOptions: ReadonlyArray<{ value: ThemeMode; ar: string; en: string; icon: string }> = [
  { value: "system", ar: "النظام", en: "System", icon: "settings" },
  { value: "dark", ar: "داكن", en: "Dark", icon: "moon" },
  { value: "light", ar: "فاتح", en: "Light", icon: "sunrise" }
];

const accentOptions: ReadonlyArray<{ value: AccentTheme; ar: string; en: string }> = [
  { value: "emerald", ar: "زمرد", en: "Emerald" },
  { value: "cyan", ar: "سيان", en: "Cyan" },
  { value: "amber", ar: "عنبر", en: "Amber" },
  { value: "rose", ar: "روز", en: "Rose" }
];

const styleOptions: ReadonlyArray<{ value: VisualStyle; ar: string; en: string }> = [
  { value: "glass", ar: "زجاج حديث", en: "Modern glass" },
  { value: "classic", ar: "بسيط", en: "Classic" },
  { value: "high-contrast", ar: "تباين عال", en: "High contrast" }
];

function label(locale: Locale, ar: string, en: string): string {
  return locale === "ar" ? ar : en;
}

function ToggleRow({ labelText, description, checked, onChange }: {
  readonly labelText: string;
  readonly description: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle-row">
      <span>
        <strong>{labelText}</strong>
        <small>{description}</small>
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.currentTarget.checked)} />
    </label>
  );
}

export function SettingsPanel({ open, onClose }: SettingsPanelProps) {
  const { settings, updateSettings } = useSettings();
  const locale = settings.locale;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="settings-panel__header">
          <div>
            <p className="eyebrow">{t(locale, "partOfPlatform")}</p>
            <h2 id="settings-title">{t(locale, "settings")}</h2>
          </div>
          <Button type="button" size="icon" variant="ghost" aria-label={locale === "ar" ? "إغلاق الإعدادات" : "Close settings"} onClick={onClose}>
            <Icon name="close" />
          </Button>
        </header>

        <section className="settings-section" aria-labelledby="settings-language">
          <h3 id="settings-language">{t(locale, "language")}</h3>
          <div className="segmented">
            {(["ar", "en"] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={settings.locale === value ? "active" : ""}
                onClick={() => updateSettings({ locale: value })}
              >
                {value === "ar" ? "العربية" : "English"}
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section" aria-labelledby="settings-theme">
          <h3 id="settings-theme">{t(locale, "theme")}</h3>
          <div className="option-grid option-grid--3">
            {themeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={settings.theme === option.value ? "option-card active" : "option-card"}
                onClick={() => updateSettings({ theme: option.value })}
              >
                <Icon name={option.icon} />
                <span>{label(locale, option.ar, option.en)}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section" aria-labelledby="settings-accent">
          <h3 id="settings-accent">{t(locale, "accent")}</h3>
          <div className="accent-row">
            {accentOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={settings.accent === option.value ? "accent-choice active" : "accent-choice"}
                data-accent={option.value}
                onClick={() => updateSettings({ accent: option.value })}
              >
                <span aria-hidden="true" />
                {label(locale, option.ar, option.en)}
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section" aria-labelledby="settings-style">
          <h3 id="settings-style">{t(locale, "visualStyle")}</h3>
          <div className="option-grid">
            {styleOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={settings.visualStyle === option.value ? "option-card active" : "option-card"}
                onClick={() => updateSettings({ visualStyle: option.value })}
              >
                {label(locale, option.ar, option.en)}
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section" aria-labelledby="settings-comfort">
          <h3 id="settings-comfort">{locale === "ar" ? "الراحة والأداء" : "Comfort and performance"}</h3>
          <div className="segmented">
            {(["default", "large"] as readonly TextSize[]).map((value) => (
              <button
                key={value}
                type="button"
                className={settings.textSize === value ? "active" : ""}
                onClick={() => updateSettings({ textSize: value })}
              >
                {value === "large" ? (locale === "ar" ? "كبير" : "Large") : (locale === "ar" ? "افتراضي" : "Default")}
              </button>
            ))}
          </div>
          <div className="segmented">
            {(["normal", "reduced"] as readonly MotionPreference[]).map((value) => (
              <button
                key={value}
                type="button"
                className={settings.motion === value ? "active" : ""}
                onClick={() => updateSettings({ motion: value })}
              >
                {value === "reduced" ? (locale === "ar" ? "مخففة" : "Reduced") : (locale === "ar" ? "عادية" : "Normal")}
              </button>
            ))}
          </div>
          <ToggleRow
            labelText={t(locale, "lightweight")}
            description={locale === "ar" ? "يقلل الشفافية والحركة للأجهزة والشبكات الأبطأ." : "Reduces transparency and motion for slower devices and networks."}
            checked={settings.lightweight}
            onChange={(lightweight) => updateSettings({ lightweight })}
          />
          <ToggleRow
            labelText={t(locale, "sounds")}
            description={locale === "ar" ? "تفضيل محفوظ للتنبيهات الصوتية عند توفرها." : "Saved preference for sound cues when supported."}
            checked={settings.sounds}
            onChange={(sounds) => updateSettings({ sounds })}
          />
          <ToggleRow
            labelText={t(locale, "notifications")}
            description={locale === "ar" ? "تفضيل محفوظ للتذكيرات المستقبلية." : "Saved preference for future reminders."}
            checked={settings.notifications}
            onChange={(notifications) => updateSettings({ notifications })}
          />
        </section>

        <section className="settings-section" aria-labelledby="settings-prayer-source">
          <h3 id="settings-prayer-source">{locale === "ar" ? "مصدر مواقيت الصلاة" : "Prayer times source"}</h3>
          <small className="settings-section__desc">
            {locale === "ar"
              ? "عُمان: يستخدم مواقيت وزارة الأوقاف الرسمية مع تحويل تلقائي للحساب الفلكي عند انقطاع الاتصال. عالمي: يحسب المواقيت فلكياً لأي موقع في العالم."
              : "Oman: uses official MARA times with automatic fallback to the calculator if unavailable. Universal: astronomically calculated, works anywhere offline."}
          </small>
          <div className="segmented">
            {(["mara", "calculated"] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={settings.prayerSource === value ? "active" : ""}
                onClick={() => updateSettings({ prayerSource: value })}
              >
                {value === "mara"
                  ? (locale === "ar" ? "عُمان (MARA)" : "Oman (MARA)")
                  : (locale === "ar" ? "عالمي (فلكي)" : "Universal (calc)")}
              </button>
            ))}
          </div>
          {settings.prayerSource === "calculated" && (
            <div className="settings-calc-method">
              <label>
                <span>{locale === "ar" ? "طريقة الحساب" : "Calculation method"}</span>
                <select
                  value={settings.calcMethod}
                  onChange={(e) => updateSettings({ calcMethod: e.currentTarget.value as CalcMethodKey })}
                >
                  {(Object.keys(CALC_METHODS) as CalcMethodKey[]).map((key) => (
                    <option key={key} value={key}>
                      {locale === "ar" ? CALC_METHODS[key].nameAr : CALC_METHODS[key].nameEn}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </section>

        <section className="settings-section" aria-labelledby="settings-hijri">
          <h3 id="settings-hijri">{t(locale, "hijriOffset")}</h3>
          <small className="settings-section__desc">{t(locale, "hijriOffsetDesc")}</small>
          <div className="segmented hijri-offset-segmented">
            {([-2, -1, 0, 1, 2] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={settings.hijriOffset === value ? "active" : ""}
                onClick={() => updateSettings({ hijriOffset: value as UserSettings["hijriOffset"] })}
              >
                {value === 0 ? (locale === "ar" ? "عُمان" : "Oman") : value > 0 ? `+${value}` : String(value)}
              </button>
            ))}
          </div>
        </section>

        <footer className="settings-panel__footer">
          <LinkButton href="https://issamawali.com" target="_blank" rel="noopener noreferrer" variant="ghost">
            Issa Al-Mawali
          </LinkButton>
          <LinkButton href="https://athkari.cc" target="_blank" rel="noopener noreferrer" variant="ghost">
            Athkari.cc
          </LinkButton>
        </footer>
      </aside>
    </div>
  );
}
